/**
 * dayCount — the traveler's day number, shown as a hanko stamp in the C5 frame.
 *
 * No day counter existed anywhere in the project before this module, so the
 * count is client-local and persisted in localStorage. It starts at Day 1 and
 * advances once per completed sleep (see SleepOverlayScene). When the server
 * grows an authoritative day number, replace `readDayCount` with a schema read
 * and keep the rest of the API.
 *
 * Pure w.r.t. Phaser; storage is injectable so tests need no DOM.
 */

export const DAY_COUNT_STORAGE_KEY = 'rpg-gibli:day-count';

export const FIRST_DAY = 1;

/** The slice of the Storage API this module needs. */
export interface DayCountStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): DayCountStorage | null {
  try {
    const ls = (globalThis as unknown as { localStorage?: DayCountStorage }).localStorage;
    return ls ?? null;
  } catch {
    return null;
  }
}

/** Coerce any stored value into a valid day number. */
export function parseDayCount(raw: string | null | undefined): number {
  if (raw === null || raw === undefined) return FIRST_DAY;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < FIRST_DAY) return FIRST_DAY;
  return Math.floor(n);
}

/** Current day number. Defaults to Day 1 when nothing is stored. */
export function readDayCount(storage: DayCountStorage | null = defaultStorage()): number {
  if (!storage) return FIRST_DAY;
  try {
    return parseDayCount(storage.getItem(DAY_COUNT_STORAGE_KEY));
  } catch {
    return FIRST_DAY;
  }
}

/** Persist an explicit day number (clamped to >= 1). Returns what was stored. */
export function writeDayCount(
  day: number,
  storage: DayCountStorage | null = defaultStorage(),
): number {
  const clamped = Number.isFinite(day) ? Math.max(FIRST_DAY, Math.floor(day)) : FIRST_DAY;
  try {
    storage?.setItem(DAY_COUNT_STORAGE_KEY, String(clamped));
  } catch {
    // best-effort
  }
  return clamped;
}

/** Advance to the next day. Returns the new day number. */
export function advanceDay(storage: DayCountStorage | null = defaultStorage()): number {
  return writeDayCount(readDayCount(storage) + 1, storage);
}
